import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * « Annuler plutôt que confirmer » (passation design §6). Une suppression courante et
 * réversible s'applique tout de suite ; un bandeau flottant propose de l'annuler pendant
 * 10 secondes, puis un accusé discret confirme le rétablissement.
 *
 * Monté une fois au niveau de l'app (UndoProvider + <UndoBar/>), piloté par useUndo().
 */
export interface UndoEntry {
    /** Ce qui vient de disparaître, nommé : « Créneau de FABRE Julien supprimé — samedi 16:00-23:30 ». */
    message: string;
    /** Rétablit ce qui a été supprimé. Peut être asynchrone. */
    undo: () => void | Promise<void>;
    /** Accusé après annulation (« Créneau rétabli »). */
    undoneMessage?: string;
    /** Durée pendant laquelle on peut annuler, en secondes (10 par défaut). */
    seconds?: number;
}

interface UndoContextValue {
    push: (entry: UndoEntry) => void;
}

const UndoContext = createContext<UndoContextValue | undefined>(undefined);

type Active = (UndoEntry & { id: number; left: number }) | null;

export function UndoProvider({ children }: { children: React.ReactNode }) {
    const [active, setActive] = useState<Active>(null);
    const [ack, setAck] = useState<string | null>(null);
    const timer = useRef<number | null>(null);
    const ackTimer = useRef<number | null>(null);
    const counter = useRef(0);

    const clearTimer = () => { if (timer.current) { window.clearInterval(timer.current); timer.current = null; } };

    const push = useCallback((entry: UndoEntry) => {
        clearTimer();
        const id = ++counter.current;
        const seconds = entry.seconds ?? 10;
        setAck(null);
        setActive({ ...entry, id, left: seconds });
        timer.current = window.setInterval(() => {
            setActive((cur) => {
                if (!cur || cur.id !== id) return cur;
                if (cur.left <= 1) { clearTimer(); return null; }
                return { ...cur, left: cur.left - 1 };
            });
        }, 1000);
    }, []);

    const dismiss = useCallback(() => { clearTimer(); setActive(null); }, []);

    const undoNow = useCallback(async () => {
        if (!active) return;
        const entry = active;
        dismiss();
        try {
            await entry.undo();
            setAck(entry.undoneMessage || 'Rétabli');
            if (ackTimer.current) window.clearTimeout(ackTimer.current);
            ackTimer.current = window.setTimeout(() => setAck(null), 2500);
        } catch (e) {
            console.error('Annulation impossible', e);
            setAck('Impossible de rétablir');
            ackTimer.current = window.setTimeout(() => setAck(null), 3000);
        }
    }, [active, dismiss]);

    useEffect(() => () => { clearTimer(); if (ackTimer.current) window.clearTimeout(ackTimer.current); }, []);

    const value = useMemo(() => ({ push }), [push]);

    return (
        <UndoContext.Provider value={value}>
            {children}
            {active && (
                <div role="status" aria-live="polite" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[120] max-w-[calc(100vw-32px)]">
                    <div className="flex flex-wrap items-center gap-3 rounded-full bg-[#15161a] text-white pl-4 pr-2 py-2 shadow-[0_18px_40px_rgba(15,20,25,.28)]">
                        <span className="text-[13px] font-medium min-w-0 truncate max-w-[60vw]">{active.message}</span>
                        <span className="text-[11px] font-semibold tabular-nums rounded-full bg-white/15 px-2 py-0.5 shrink-0" aria-label={`${active.left} secondes`}>{active.left} s</span>
                        <button type="button" onClick={undoNow} className="h-8 px-3.5 rounded-full bg-white text-[#15161a] text-[13px] font-semibold whitespace-nowrap shrink-0 hover:-translate-y-px transition-transform">Annuler</button>
                        <button type="button" onClick={dismiss} aria-label="Fermer" className="w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 shrink-0"><X size={15} /></button>
                    </div>
                </div>
            )}
            {!active && ack && (
                <div role="status" aria-live="polite" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[120]">
                    <div className="rounded-full bg-[#15161a] text-white/90 px-4 py-2 text-[12.5px] shadow-[0_18px_40px_rgba(15,20,25,.28)]">{ack}</div>
                </div>
            )}
        </UndoContext.Provider>
    );
}

export function useUndo(): UndoContextValue {
    const ctx = useContext(UndoContext);
    if (!ctx) throw new Error('useUndo doit être utilisé sous UndoProvider');
    return ctx;
}
